import React, { useCallback, useRef, useState } from 'react';
import { ACCEPTED_MIME_TYPES, MAX_FILE_SIZE_MB } from '../config';

interface FileDropzoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}

function validate(file: File): string | null {
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return `${file.name} is larger than the ${MAX_FILE_SIZE_MB} MB limit.`;
  }
  if (file.type && !ACCEPTED_MIME_TYPES.includes(file.type)) {
    return `${file.name}: unsupported file type "${file.type}".`;
  }
  return null;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

const FileDropzone: React.FC<FileDropzoneProps> = ({ files, onChange, disabled }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addFiles = useCallback(
    (incoming: FileList | File[] | null) => {
      setError(null);
      const list = incoming ? Array.from(incoming) : [];
      if (list.length === 0) return;

      const errors: string[] = [];
      const valid: File[] = [];
      list.forEach((f) => {
        const err = validate(f);
        if (err) errors.push(err);
        else valid.push(f);
      });
      if (errors.length) setError(errors.join(' '));

      // De-duplicate by name+size so re-selecting the same file is a no-op.
      const seen = new Set(files.map((f) => `${f.name}:${f.size}`));
      const merged = [...files];
      valid.forEach((f) => {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(f);
        }
      });
      onChange(merged);
    },
    [files, onChange]
  );

  const removeAt = (index: number) => onChange(files.filter((_, i) => i !== index));

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (!disabled) addFiles(e.dataTransfer.files);
    },
    [disabled, addFiles]
  );

  return (
    <div>
      <div
        className={`dropzone${dragging ? ' dropzone--active' : ''}${
          disabled ? ' dropzone--disabled' : ''
        }`}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) inputRef.current?.click();
        }}
        aria-label="Upload documents"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_MIME_TYPES.join(',')}
          multiple
          hidden
          disabled={disabled}
          onChange={(e) => {
            addFiles(e.target.files);
            if (inputRef.current) inputRef.current.value = ''; // allow re-selecting
          }}
        />
        <div className="dropzone__prompt">
          <div className="dropzone__icon">⇪</div>
          <p>
            <strong>Drop files here</strong> or click to browse
          </p>
          <p className="muted">PDF or image · up to {MAX_FILE_SIZE_MB} MB each · multiple allowed</p>
        </div>
      </div>

      {files.length > 0 && (
        <ul className="filelist">
          {files.map((f, i) => (
            <li className="filelist__item" key={`${f.name}:${f.size}:${i}`}>
              <span className="filelist__name" title={f.name}>
                {f.name}
              </span>
              <span className="muted">{formatSize(f.size)}</span>
              <button
                type="button"
                className="icon-btn"
                disabled={disabled}
                title="Remove"
                onClick={() => removeAt(i)}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="field-error">{error}</p>}
    </div>
  );
};

export default FileDropzone;
